/**
 * src/lib/hooks/useUndoableSend.js — TICKET-855, extended by TICKET-858.
 *
 * One send experience for every outbound email surface: a countdown toast with an
 * Undo button, then a plain-language SENT or FAILED toast. Before this, the History
 * reply composer reported neither — it closed itself on success and did absolutely
 * nothing on failure, so a reply blocked by a 403 looked exactly like a reply that
 * had gone out (CEO 2026-09-21, Agustina's reply).
 *
 * TICKET-858 adds the other half of "don't lose my email": the text is snapshotted
 * to localStorage for the life of the Undo window, and this hook decides where it
 * goes when the window closes —
 *   sent      → the draft is cleared; the email is in the deal's history now.
 *   cancelled → the text goes BACK to the composer's draft, because Undo means
 *               "let me change it", not "throw it away".
 *   failed    → same: the words the user wrote are never the thing that is lost.
 *   crash     → nothing resolves, the snapshot outlives the session, and
 *               recoverPendingEmails() (AppShell) restores it at next start.
 *
 * The queue itself lives in src/lib/undoSend.js (module-level, survives the
 * composer unmounting). This hook only wires it to the toast layer + i18n.
 */
import { useCallback } from 'react';
import { useToast } from '../../components/shared/ToastProvider.jsx';
import { useT } from '../i18n/I18nContext.jsx';
import { queueSend, restoreToDraft, clearDraft, UNDO_WINDOW_MS } from '../undoSend';

/** Default success test. Overridden by `isOk` for actions with a different wire shape. */
const defaultIsOk = (res) => !!res?.ok;

export default function useUndoableSend() {
  const { showToast, showSuccess, showError, showInfo, dismiss } = useToast();
  const { t } = useT();

  /**
   * @param {() => Promise<any>} run the API call to hold
   * @param {{ to?:string, onSent?:(res:any)=>void, mapError?:(res:any)=>string|null,
   *           isOk?:(res:any)=>boolean,
   *           snapshot?:{ draftKey:string, to?:string, subject?:string, body?:string } }} [opts]
   *   `mapError` lets a caller translate a known server error (e.g. "Gmail not
   *   configured") into its own message instead of the generic one. `isOk` exists
   *   because not every send action answers `{ ok }` — the quote path answers
   *   `{ success, sent }`, and treating that as a failure would have re-created
   *   the exact silence this hook was built to remove.
   */
  return useCallback((run, { to = '', onSent, mapError, isOk = defaultIsOk, snapshot = null } = {}) => {
    const job = queueSend(run, { snapshot });
    // TICKET-1366: a double tap re-queued the same email; it is ONE send with ONE toast.
    if (job.deduped) return job;

    const toastId = showToast({
      severity: 'info',
      message: t('emailSend.pending', { to, seconds: Math.round(UNDO_WINDOW_MS / 1000) }),
      durationMs: UNDO_WINDOW_MS,
      action: {
        label: t('emailSend.undo'),
        // cancel() is false once the window has closed — say so rather than
        // letting the CEO believe an email was pulled back that already left.
        onClick: () => {
          if (job.cancel()) {
            restoreToDraft(snapshot);
            showInfo(t('emailSend.canceled'));
          } else {
            showInfo(t('emailSend.tooLate'));
          }
        },
      },
    });

    job.promise.then((outcome) => {
      dismiss(toastId);
      if (outcome.status === 'canceled') return; // the Undo handler already spoke
      if (outcome.status === 'failed') {
        restoreToDraft(snapshot);
        showError(t('emailSend.error'), { critical: true });
        return;
      }
      const res = outcome.result;
      if (isOk(res)) {
        if (res?.verified === false) {
          // TICKET-1170: Gmail's copy came back EMPTY. Never announce that as a good
          // send, and keep the words — Gmail does not have them either.
          restoreToDraft(snapshot);
          showError(t('emailSend.sentEmpty', { to }), { critical: true });
        } else {
          clearDraft(snapshot?.draftKey);
          showSuccess(t('emailSend.sent', { to }));
        }
        onSent?.(res);
      } else {
        restoreToDraft(snapshot);
        // Ensure error message is a string — res.error might be an object
        // (TICKET-964: hardening against speculative error shapes from older API versions)
        const errorMsg = mapError?.(res) || (typeof res?.error === 'string' ? res.error : null) || t('emailSend.error');
        showError(errorMsg, { critical: true });
      }
    });

    return job;
  }, [showToast, showSuccess, showError, showInfo, dismiss, t]);
}
